import { useState, useEffect } from 'react';
import Shell from '../components/Shell';
import { C2Section } from '../components/c2';
import type { DecorShape } from '../components/c2/Section';
import type { PastelColor } from '../components/c2/Pill';
import PageHero from '../components/secondary/PageHero';
import BottomCTA from '../components/secondary/BottomCTA';

interface FAQItem { q: string; a: string; id?: string }
interface FAQSection { title: string; emoji: string; color: PastelColor; items: FAQItem[] }

const faqSections: FAQSection[] = [
  {
    title: 'Getting started', emoji: '🚀', color: 'peach',
    items: [
      { q: 'How do I set up go/ links for my team?',
        a: 'Sign up with your work email, install the Chrome extension, and start creating go/ links in minutes. Your whole team can join by signing up with the same email domain — no admin invite required.' },
      { q: 'Do I need to host a server?',
        a: "Nope. We host it for you, free. If you'd rather self-host, our code is MIT-licensed on GitHub and runs on Node + SQLite." },
      { q: 'Does typing go/something in the address bar actually work?',
        a: 'Yes. The Chrome extension registers go as a custom protocol, so typing go/roadmap works from any tab, any site. No prefix, no copy-paste.' },
    ],
  },
  {
    title: 'For your team', emoji: '👥', color: 'sky',
    items: [
      { q: 'Who can create or edit a go/ link?',
        a: "Any signed-in workspace member can create links. Edit & delete rights are limited to the creator." },
      { q: 'How do you prevent naming collisions?',
        a: "Go/ link names are first-come-first-served within a workspace. You'll see a clear error if someone else already claimed the name, with a one-click jump to their link." },
      { id: 'alternates', q: 'Can multiple go/ links point to the same URL?',
        a: 'Yes — we call these alternates. Add go/roadmap, go/plan, and go/q4-plan as aliases of one destination. Rename the primary and all alternates update automatically.' },
      { q: 'What happens when someone leaves the company?',
        a: 'When someone leaves your organization, their go/ links remain and can be claimed by any team member.' },
    ],
  },
  {
    title: 'Privacy & security', emoji: '🔒', color: 'mint',
    items: [
      { q: 'What data do you store?',
        a: 'Email, name, go/ link name, destination URL, and a click count per link. Nothing else. No tracking pixels on redirects.' },
      { q: 'Who can see our go/ links?',
        a: 'Only members of your workspace (same email domain) can see your go/ links.' },
      { q: 'Do you sell or share data?',
        a: "No. Tidy Go Links is not for profit. We don't sell data, nor do we have data that anyone would want to buy. (But even if they did, we wouldn't sell your data.)" },
      { q: 'Can I export our data?',
        a: 'Any admin can export all workspace links as CSV or JSON from Settings. Your data, always.' },
    ],
  },
  {
    title: 'Pricing', emoji: '💰', color: 'lemon',
    items: [
      { q: 'Is it really free?',
        a: 'Yes. Free forever for unlimited users. No freemium trap, no feature paywall.' },
      { q: 'How do you make money?',
        a: "We don't — we're a not for profit open-source collective. Our costs for this and other projects are covered by us. This site really doesn't cost much to run!" },
      { q: "What's the catch?",
        a: 'There isn\'t one. The only "catch" is we don\'t guarantee you fast support if you have a question. Usually we reply same day, but not if we\'re on vacation or cranking on another project.' },
    ],
  },
];

const pastelBg: Record<PastelColor, string> = {
  peach: 'var(--color-peach)',
  mint: 'var(--color-mint)',
  sky: 'var(--color-sky)',
  lemon: 'var(--color-lemon)',
  lilac: 'var(--color-lilac)',
};

const faqDecor: DecorShape[] = [
  { shape: 'circle', size: 220, color: 'peach', style: { position: 'absolute', top: 80, left: -90, opacity: 0.45 } },
  { shape: 'pill', size: 240, color: 'sky', style: { position: 'absolute', top: 280, right: -70, opacity: 0.45, transform: 'rotate(8deg)' } },
  { shape: 'square', size: 140, color: 'mint', style: { position: 'absolute', top: 720, left: -50, opacity: 0.5, transform: 'rotate(-10deg)' } },
  { shape: 'circle', size: 240, color: 'lemon', style: { position: 'absolute', top: 1040, right: -110, opacity: 0.45 } },
  { shape: 'pill', size: 220, color: 'lilac', style: { position: 'absolute', top: 1520, left: -70, opacity: 0.5, transform: 'rotate(-8deg)' } },
  { shape: 'circle', size: 180, color: 'peach', style: { position: 'absolute', top: 1980, right: -50, opacity: 0.5 } },
  { shape: 'square', size: 130, color: 'sky', style: { position: 'absolute', top: 2400, left: -60, opacity: 0.5, transform: 'rotate(12deg)' } },
  { shape: 'circle', size: 220, color: 'mint', style: { position: 'absolute', bottom: 60, right: -80, opacity: 0.5 } },
];

export default function FAQPage() {
  const [open, setOpen] = useState<Record<string, boolean>>({ '0-0': true });
  const toggle = (k: string) => setOpen(o => ({ ...o, [k]: !o[k] }));

  // Deep-link: open the question matching #hash and scroll to it.
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;
    let targetKey: string | null = null;
    faqSections.forEach((section, si) => {
      section.items.forEach((item, qi) => {
        if (item.id === hash) targetKey = `${si}-${qi}`;
      });
    });
    if (!targetKey) return;
    setOpen(o => ({ ...o, [targetKey!]: true }));
    setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  }, []);

  return (
    <Shell>
      <PageHero
        title="Q & A"
        titleAccentWord="&"
        sub="If we're missing something, email support@tidygolinks.com — real humans, usually same-day."
        decorShapes={[
          { shape: 'circle', size: 280, color: 'lilac', style: { position: 'absolute', top: -40, right: -80, opacity: 0.5 } },
          { shape: 'circle', size: 180, color: 'lemon', style: { position: 'absolute', top: 100, left: -40, opacity: 0.55 } },
          { shape: 'square', size: 120, color: 'mint', style: { position: 'absolute', bottom: 20, right: '20%', opacity: 0.5, transform: 'rotate(12deg)' } },
        ]}
      />

      <C2Section pad={40} decor={faqDecor}>
        <div className="flex flex-col gap-12 max-w-[820px] mx-auto">
          {faqSections.map((section, si) => (
            <div key={section.title}>
              <div className="flex items-center gap-3.5 mb-5">
                <div
                  className="w-11 h-11 rounded-md flex items-center justify-center text-[22px]"
                  style={{ background: pastelBg[section.color] }}
                >
                  {section.emoji}
                </div>
                <h2
                  className="font-display font-bold text-ink m-0"
                  style={{ fontSize: 28, letterSpacing: '-0.01em' }}
                >
                  {section.title}
                </h2>
              </div>
              <div className="flex flex-col gap-2">
                {section.items.map((item, qi) => {
                  const key = `${si}-${qi}`;
                  const isOpen = !!open[key];
                  return (
                    <div
                      key={qi}
                      id={item.id}
                      className="bg-surface rounded-lg overflow-hidden transition-colors duration-150"
                      style={{ border: `1px solid ${isOpen ? 'var(--accent-soft)' : 'var(--color-hairline)'}`, scrollMarginTop: 100 }}
                    >
                      <button
                        onClick={() => toggle(key)}
                        className="w-full flex justify-between items-center bg-transparent border-0 cursor-pointer text-left"
                        style={{ padding: '20px 24px' }}
                      >
                        <span className="text-base font-semibold text-ink leading-[1.4]">{item.q}</span>
                        <span
                          className="shrink-0 ml-4 w-7 h-7 rounded-full flex items-center justify-center font-bold transition-all duration-150"
                          style={{
                            background: isOpen ? 'var(--accent-soft)' : 'var(--color-bg-alt)',
                            color: isOpen ? 'var(--accent-deep)' : 'var(--color-ink-soft)',
                            fontSize: 16,
                            transform: isOpen ? 'rotate(45deg)' : 'rotate(0)',
                          }}
                        >
                          +
                        </span>
                      </button>
                      {isOpen && (
                        <div
                          className="text-[15px] text-ink-soft leading-[1.65]"
                          style={{ padding: '0 24px 22px' }}
                        >
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </C2Section>

      <BottomCTA
        title="Can't find what you're looking for?"
        sub="Email support@tidygolinks.com — we actually read & reply to every message, usually on the same day."
        primaryLabel="Contact support"
        primaryTo="/support"
      />
    </Shell>
  );
}
